import { useCallback, useRef } from 'react';
import { v4 as uuid } from 'uuid';
import { usePlayerState } from './usePlayerState';
import { t } from 'i18next';

export const useAudioPlayer = () => {
  const { isLoading, isPlaying, currentTime, duration, error, ref } = usePlayerState();
  const playerRef = useRef(ref.current);

  const audioRef = useRef(document.createElement('audio'));
  audioRef.current.crossOrigin = 'anonymous';
  const uidRef = useRef('');

  const reset = useCallback(() => {
    uidRef.current = uuid();
    try {
      const audio = audioRef.current;
      if (audio) {
        audio.onerror = null;
        audio.onplay = null;
        audio.onpause = null;
        audio.onloadedmetadata = null;
        audio.oncanplaythrough = null;
        audio.ontimeupdate = null;
        audio.pause();
      }
    } catch (e) {

    }

    playerRef.current.reset();
  }, []);

  const load = useCallback(async (url: string | (() => Promise<string>), autoPlay = true) => {
    reset();
    const audio = audioRef.current;
    if (!audio) {
      return;
    }

    playerRef.current.loadStart();
    const curUid = uidRef.current;

    try {
      let urlStr = url;
      if (typeof url === 'function') {
        urlStr = await url();
      }

      if (uidRef.current !== curUid) {
        return;
      }

      if (!urlStr) {
        throw new Error(t('url不存在'));
      }

      audio.src = urlStr as string;
      audio.onloadeddata = () => {
        if (uidRef.current !== curUid) {
          return;
        }
        // 确保 duration 是有效的数字
        if (audio.duration && !isNaN(audio.duration) && isFinite(audio.duration)) {
          playerRef.current.setDuration(audio.duration);
        }
      };
      audio.onloadedmetadata = () => {
        if (uidRef.current !== curUid) {
          return;
        }
        // 确保 duration 是有效的数字
        if (audio.duration && !isNaN(audio.duration) && isFinite(audio.duration)) {
          playerRef.current.setDuration(audio.duration);
        }
      };
      audio.ondurationchange = () => {
        if (uidRef.current !== curUid) {
          return;
        }
        // 确保 duration 是有效的数字
        if (audio.duration && !isNaN(audio.duration) && isFinite(audio.duration)) {
          playerRef.current.setDuration(audio.duration);
        }
      };
      audio.oncanplaythrough = () => {
        if (uidRef.current !== curUid) {
          return;
        }
        // 再次检查 duration
        if (audio.duration && !isNaN(audio.duration) && isFinite(audio.duration)) {
          playerRef.current.setDuration(audio.duration);
        }
        playerRef.current.loadEnd();
        if (autoPlay) {
          audio.play();
        }
      };
      audio.onerror = () => {
        if (uidRef.current !== curUid) {
          return;
        }
        playerRef.current.setError(new Error(audio?.error?.message));
      };
      audio.onplay = () => {
        if (uidRef.current !== curUid) {
          return;
        }
        playerRef.current.play();
        playerRef.current.setCurrentTime(audio.currentTime);
      };
      audio.onpause = () => {
        if (uidRef.current !== curUid) {
          return;
        }
        playerRef.current.pause();
        playerRef.current.setCurrentTime(audio.currentTime);
      };
      audio.ontimeupdate = () => {
        if (uidRef.current !== curUid) {
          return;
        }
        playerRef.current.setCurrentTime(audio.currentTime);
      };
    } catch (e) {
      if (uidRef.current !== curUid) {
        return;
      }

      playerRef.current.setError(new Error((e as any)?.message));
    }
  }, [reset]);

  const pause = useCallback(() => {
    try {
      const audio = audioRef.current;
      if (!audio) {
        return;
      }
      audio.pause();
    } catch (e) {

    }
  }, []);

  const seek = useCallback((currentTime) => {
    try {
      const audio = audioRef.current;
      if (!audio) {
        return;
      }
      pause();
      audio.currentTime = currentTime;
    } catch (e) {

    }
  }, [pause]);

  const play = useCallback(() => {
    try {
      const audio = audioRef.current;
      if (!audio) {
        return;
      }
      audio.play();
    } catch (e) {

    }
  }, []);

  return {
    reset,
    play,
    pause,
    seek,
    load,
    error,
    isLoading,
    isPlaying,
    isPausing: !isPlaying,
    currentTime,
    duration,
  };
};
